/**
 * Debug page for designing and testing the bracket view component.
 * Route: #bracketviewdebug
 */

import { ArrowLeft, Monitor, Smartphone, Tablet, Trophy } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  BracketViewCanvas,
  BracketViewMobile,
} from '@/components/tournament/BracketView';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getCountryByCode } from '@/data/countries';
import type { CountryCode } from '@/data/countries/types';
import type { BracketMatch } from '@/lib/tournament/types';
import { cn } from '@/lib/utils';

// Oceania country codes for mock data
const oceaniaCountries: CountryCode[] = [
  'AU',
  'NZ',
  'FJ',
  'PG',
  'WS',
  'TO',
  'VU',
  'SB',
  'KI',
  'FM',
  'PW',
  'MH',
  'NR',
  'TV',
  'NC',
  'PF',
];

// Generate a completed 16-team bracket for testing
function generateMockBracket(): BracketMatch[] {
  const matches: BracketMatch[] = [];
  const countries = [...oceaniaCountries];

  // Round 1 (Round of 16) - 8 matches
  const r1Winners: CountryCode[] = [];
  for (let i = 0; i < 8; i++) {
    const countryA = countries[i * 2];
    const countryB = countries[i * 2 + 1];
    // Simulate winner (pick one randomly but deterministically)
    const winner = i % 2 === 0 ? countryA : countryB;
    r1Winners.push(winner);
    matches.push({
      id: `match-r1-m${i + 1}`,
      round: 1,
      position: i + 1,
      countryA,
      countryB,
      winner,
    });
  }

  // Round 2 (Quarter-finals) - 4 matches
  const r2Winners: CountryCode[] = [];
  for (let i = 0; i < 4; i++) {
    const countryA = r1Winners[i * 2];
    const countryB = r1Winners[i * 2 + 1];
    const winner = i % 2 === 0 ? countryA : countryB;
    r2Winners.push(winner);
    matches.push({
      id: `match-r2-m${i + 1}`,
      round: 2,
      position: i + 1,
      countryA,
      countryB,
      winner,
    });
  }

  // Round 3 (Semi-finals) - 2 matches
  const r3Winners: CountryCode[] = [];
  const r3Losers: CountryCode[] = [];
  for (let i = 0; i < 2; i++) {
    const countryA = r2Winners[i * 2];
    const countryB = r2Winners[i * 2 + 1];
    const winner = i === 0 ? countryA : countryB;
    const loser = winner === countryA ? countryB : countryA;
    r3Winners.push(winner);
    r3Losers.push(loser);
    matches.push({
      id: `match-r3-m${i + 1}`,
      round: 3,
      position: i + 1,
      countryA,
      countryB,
      winner,
    });
  }

  // Third place match
  matches.push({
    id: 'match-third-place',
    round: 4,
    position: 0,
    countryA: r3Losers[0],
    countryB: r3Losers[1],
    winner: r3Losers[0],
    isThirdPlace: true,
  });

  // Round 4 (Final) - 1 match
  matches.push({
    id: `match-r4-m1`,
    round: 4,
    position: 1,
    countryA: r3Winners[0],
    countryB: r3Winners[1],
    winner: r3Winners[0],
  });

  return matches;
}

// Podium mockup component
interface PodiumPlace {
  position: number;
  code: CountryCode;
}

function PodiumMockup({ places }: { places: PodiumPlace[] }) {
  const first = places.find((p) => p.position === 1);
  const second = places.find((p) => p.position === 2);
  const third = places.find((p) => p.position === 3);
  const fourth = places.find((p) => p.position === 4);

  const firstCountry = first ? getCountryByCode(first.code) : null;
  const secondCountry = second ? getCountryByCode(second.code) : null;
  const thirdCountry = third ? getCountryByCode(third.code) : null;
  const fourthCountry = fourth ? getCountryByCode(fourth.code) : null;

  // Podium heights (relative)
  const heights = {
    first: 120,
    second: 90,
    third: 70,
    fourth: 50,
  };

  return (
    <div className="flex flex-col items-center gap-4 p-6">
      {/* Podium - classic 2-1-3 layout with 4th */}
      <div className="flex items-end justify-center gap-2">
        {/* 2nd Place */}
        <div className="flex flex-col items-center">
          {secondCountry && (
            <div className="flex flex-col items-center mb-2">
              <span className="text-3xl mb-1">{secondCountry.flag}</span>
              <span className="text-xs font-medium text-center max-w-[80px] truncate">
                {secondCountry.name}
              </span>
            </div>
          )}
          <div
            className="w-24 bg-gradient-to-b from-gray-300 to-gray-400 rounded-t-lg flex items-start justify-center pt-3 border-2 border-gray-400 border-b-0"
            style={{ height: `${heights.second}px` }}
          >
            <span className="text-2xl">🥈</span>
          </div>
        </div>

        {/* 1st Place */}
        <div className="flex flex-col items-center">
          {firstCountry && (
            <div className="flex flex-col items-center mb-2">
              <span className="text-4xl mb-1">{firstCountry.flag}</span>
              <span className="text-sm font-bold text-center max-w-[100px]">
                {firstCountry.name}
              </span>
            </div>
          )}
          <div
            className="w-28 bg-gradient-to-b from-yellow-300 to-yellow-500 rounded-t-lg flex items-start justify-center pt-3 border-2 border-yellow-500 border-b-0 shadow-lg"
            style={{ height: `${heights.first}px` }}
          >
            <span className="text-3xl">🏆</span>
          </div>
        </div>

        {/* 3rd Place */}
        <div className="flex flex-col items-center">
          {thirdCountry && (
            <div className="flex flex-col items-center mb-2">
              <span className="text-3xl mb-1">{thirdCountry.flag}</span>
              <span className="text-xs font-medium text-center max-w-[80px] truncate">
                {thirdCountry.name}
              </span>
            </div>
          )}
          <div
            className="w-24 bg-gradient-to-b from-amber-500 to-amber-700 rounded-t-lg flex items-start justify-center pt-3 border-2 border-amber-600 border-b-0"
            style={{ height: `${heights.third}px` }}
          >
            <span className="text-2xl">🥉</span>
          </div>
        </div>

        {/* 4th Place */}
        <div className="flex flex-col items-center ml-2">
          {fourthCountry && (
            <div className="flex flex-col items-center mb-2">
              <span className="text-2xl mb-1">{fourthCountry.flag}</span>
              <span className="text-[10px] font-medium text-center max-w-[70px] truncate text-muted-foreground">
                {fourthCountry.name}
              </span>
            </div>
          )}
          <div
            className="w-20 bg-gradient-to-b from-stone-300 to-stone-400 rounded-t-lg flex items-start justify-center pt-3 border-2 border-stone-400 border-b-0"
            style={{ height: `${heights.fourth}px` }}
          >
            <span className="text-xl font-bold text-stone-600">4</span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface BracketViewDebugProps {
  onBack: () => void;
}

export function BracketViewDebug({ onBack }: BracketViewDebugProps) {
  const [viewMode, setViewMode] = useState<'bracket' | 'podium'>('podium');
  const [deviceMode, setDeviceMode] = useState<'desktop' | 'tablet' | 'mobile'>(
    'desktop',
  );
  const mockBracket = useMemo(() => generateMockBracket(), []);

  // Extract top 4 from mock bracket for podium
  const mockPodiumPlaces = useMemo(() => {
    const finalMatch = mockBracket.find(
      (m) => m.round === 4 && !m.isThirdPlace,
    );
    const thirdPlaceMatch = mockBracket.find((m) => m.isThirdPlace);

    if (!finalMatch?.winner) return [];

    const first = finalMatch.winner;
    const second =
      finalMatch.countryA === first ? finalMatch.countryB : finalMatch.countryA;
    const third = thirdPlaceMatch?.winner;
    const fourth = thirdPlaceMatch
      ? thirdPlaceMatch.countryA === third
        ? thirdPlaceMatch.countryB
        : thirdPlaceMatch.countryA
      : undefined;

    return [
      { position: 1, code: first },
      { position: 2, code: second },
      ...(third ? [{ position: 3, code: third }] : []),
      ...(fourth ? [{ position: 4, code: fourth }] : []),
    ];
  }, [mockBracket]);

  return (
    <div className="h-dvh w-full flex flex-col bg-stone-100">
      {/* Header */}
      <header className="shrink-0 bg-white border-b p-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-display text-xl font-bold">
              Tournament View Debug
            </h1>
            <p className="text-sm text-muted-foreground">
              Design and test tournament components
            </p>
          </div>
          {/* View mode toggle */}
          <Tabs
            value={viewMode}
            onValueChange={(v) => setViewMode(v as 'bracket' | 'podium')}
          >
            <TabsList>
              <TabsTrigger value="podium" className="gap-1.5">
                <Trophy className="w-4 h-4" />
                <span className="hidden sm:inline">Podium</span>
              </TabsTrigger>
              <TabsTrigger value="bracket" className="gap-1.5">
                <Monitor className="w-4 h-4" />
                <span className="hidden sm:inline">Bracket</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {/* Device mode toggle (only for bracket) */}
          {viewMode === 'bracket' && (
            <Tabs
              value={deviceMode}
              onValueChange={(v) =>
                setDeviceMode(v as 'desktop' | 'tablet' | 'mobile')
              }
            >
              <TabsList>
                <TabsTrigger value="desktop" className="gap-1.5">
                  <Monitor className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger value="tablet" className="gap-1.5">
                  <Tablet className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger value="mobile" className="gap-1.5">
                  <Smartphone className="w-4 h-4" />
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 min-h-0 p-4">
        <Card className="h-full overflow-hidden">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              {viewMode === 'podium' ? (
                <>🏆 Podium Mockup (Top 4)</>
              ) : (
                <>
                  🏆 Tournament Bracket (Oceania - 16 countries)
                  {deviceMode !== 'mobile' && (
                    <span className="text-xs font-normal text-muted-foreground ml-2">
                      Scroll to zoom, drag to pan
                    </span>
                  )}
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 h-[calc(100%-3.5rem)]">
            {viewMode === 'podium' ? (
              <div className="h-full flex items-center justify-center bg-gradient-to-b from-stone-50 to-stone-100">
                <PodiumMockup places={mockPodiumPlaces} />
              </div>
            ) : deviceMode === 'mobile' ? (
              <div className="max-w-sm mx-auto h-full border-x bg-white">
                <BracketViewMobile bracket={mockBracket} className="h-full" />
              </div>
            ) : deviceMode === 'tablet' ? (
              <div className="max-w-3xl mx-auto h-full border-x bg-stone-50">
                <BracketViewCanvas bracket={mockBracket} className="h-full" />
              </div>
            ) : (
              <BracketViewCanvas bracket={mockBracket} className="h-full" />
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default BracketViewDebug;
